import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import {SuccessResponse} from 'common/report/retention/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {retentionResponseHasData} from 'common/report/retention/util/request';
import {QueryContext, SegmentationMetadata} from 'common/query-context';
import {BaseQueryErrorType} from 'common/report/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {isQueryEmpty, RetentionQuery} from 'common/report/retention/models/retention-query';

import {ChartType, ReportName} from '../constants';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {ReportManager} from '../report-manager';
import {getReportTracker} from '../report-manager/tracking';
import {CustomEventDefinition} from '../util/saved-entities/types';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {RetentionQueryManager} from './query';
import {RetentionReportHeaderEl} from './report-header';
import {
  getRetentionQueryFromQueryContext,
  getRetentionReportQueryContext,
  RetentionQueryBuilderEl,
} from './query-builder';
import {RetentionVisualizationEl} from './visualization';
import {getQueryTrackingProperties} from './tracking';

export class RetentionReportManager extends ReportManager<
  RetentionBookmarkParams,
  SuccessResponse,
  BaseQueryErrorType
> {
  bookmarkType = BookmarkType.Retention;
  reportDisplayName = `Retention`;

  tracker = getReportTracker({
    appName: ReportName.Retention,
    productionPrefix: this.reportDisplayName,
  });

  isCsvExportEnabled = true;

  reportHeaderEl = RetentionReportHeaderEl;
  queryBuilderEl = RetentionQueryBuilderEl;
  visualizationEl = RetentionVisualizationEl;

  queryManager = new RetentionQueryManager({projectId: this.projectId, workspaceId: this.workspaceId});

  public hasUnsavedBookmarkParams(original: RetentionBookmarkParams, edited: RetentionBookmarkParams) {
    // TODO so much superfluous model-bookmark-model conversion; make helpers that operate just on
    // bookmarks
    return !RetentionQuery.createFromBookmark(original).equals(RetentionQuery.createFromBookmark(edited));
  }

  public getDefaultBookmarkParams(_project: CurrentProject): RetentionBookmarkParams {
    return new RetentionQuery({}).generateBookmark();
  }

  public getSegmentationMetadatas(response: SuccessResponse): SegmentationMetadata[] {
    const metas = response.metadata?.group_by_metadata;
    return metas?.map?.((meta) => ({
      segment: meta.bookmark,
      numericBucket: meta.numeric_buckets,
    }));
  }

  public handleSegmentFilterControllerChange(
    segmentFilterController: SegmentFilterController,
    params: RetentionBookmarkParams,
  ) {
    const selectedSegments = segmentFilterController.getSelectedItems(0).map((item) => item.id);
    return {...params, selected_segments: selectedSegments};
  }
  public shouldResetSegmentFilterSelectMode(original: RetentionBookmarkParams, edited: RetentionBookmarkParams) {
    return original.group_by?.length !== edited.group_by?.length;
  }

  public isQueryable(params: RetentionBookmarkParams) {
    return !isQueryEmpty(params);
  }

  public isEmptyResults(response: SuccessResponse) {
    return !retentionResponseHasData(response);
  }

  public getDateRange(params: RetentionBookmarkParams): DateRange {
    return DateRange.fromBookmarkFormat(RetentionQuery.createFromBookmark(params).dateRange);
  }

  public getBookmarkParamsFromQueryContext(context: QueryContext): RetentionBookmarkParams {
    return getRetentionQueryFromQueryContext(context).generateBookmark();
  }

  public getQueryContextFromBookmarkParams({
    params,
    originDashboard,
    querySamplingEnabled,
    customEvents,
  }: {
    params: RetentionBookmarkParams;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
    customEvents: CustomEventDefinition[];
  }) {
    const query = RetentionQuery.createFromBookmark(params);
    query.querySampling = querySamplingEnabled;
    return getRetentionReportQueryContext(query, {originDashboard, customEvents});
  }

  public hasDataTable(params: RetentionBookmarkParams) {
    const query = RetentionQuery.createFromBookmark(params);
    return (
      query.isRetentionCurveBasedChart() ||
      [ChartType.RetentionTrendMetric, ChartType.RetentionTrend].includes(query.chartType)
    );
  }

  public getQuerySamplingFactor(results: SuccessResponse): number {
    return results?.metadata?.min_sampling_factor ?? 1;
  }

  public getClientQueryTrackingProps(params: RetentionBookmarkParams): ClientQueryTrackingProps {
    const query = RetentionQuery.createFromBookmark(params);
    return getQueryTrackingProperties(query);
  }
}
